/**
 * <DocumentRefBlock> — inline pointer to an AIEditor document, with
 * optional line range + preview. SPA-originated block (the outbound
 * composer doesn't emit these yet; the wire shape is registered for
 * future bidirectional parity).
 *
 * **No live callers today (intentional scaffolding).** Registered with
 * the BlockRenderer dispatcher so the union is exhaustive at compile
 * time; first live caller will be the document-reference outbound
 * slice (plan §6 followup — pair-with the outbound-composer redesign).
 *
 * v1 renders as a card with title + line range + small preview snippet.
 * Click-to-open in WorkspaceFilePane is a followup (wire to the
 * existing `lx_open_file` flow).
 */
import { FileText } from "lucide-react";
import type { DocumentRefBlock as DocumentRefBlockType } from "@/types/content-blocks";

interface DocumentRefBlockProps {
  block: DocumentRefBlockType;
}

export function DocumentRefBlock({ block }: DocumentRefBlockProps) {
  const range =
    block.startLine != null && block.endLine != null
      ? ` (lines ${block.startLine}–${block.endLine})`
      : "";
  return (
    <div
      data-testid="document-ref-block"
      data-doc-id={block.docId}
      className="my-1 rounded-md border border-cream-300 bg-cream-100/30 px-3 py-2 text-body-sm dark:border-coffee-700 dark:bg-coffee-900/30"
    >
      <div className="flex items-center gap-1.5 text-caption text-coffee-600 dark:text-cream-400">
        <FileText size={12} />
        <span className="font-mono">{block.docId}</span>
        <span className="text-coffee-500">{range}</span>
      </div>
      {block.preview && (
        <div className="mt-1 whitespace-pre-wrap text-coffee-700 dark:text-cream-300">
          {block.preview}
        </div>
      )}
    </div>
  );
}
